import { prepareSearchPhrase } from 'shared/utils/queryParams.utils';
import { trackSearchResultImpression, trackSearchResultClick } from "shared/services/epifind/searchTrackingService";
import axios from "axios";

const parameters = {
	limit: "ItemsPerPage",
	page: "currentPage",
	sort: "sortBy"
};

function resetCurrentPageIfNeeded(state) {
	if (!state.isInitializingFromQueryString) {
		state.currentPage = 1;
	}
}

function removeSelectedFacet(state, groupIdToRemove) {
	const newFacets = [];
	for (const facet of state.selectedFacets) {
		if (facet.groupId !== groupIdToRemove) {
			newFacets.push(facet);
		}
	}
	state.selectedFacets = newFacets;
}

function findGroupAndFacet(facetGroups, groupId, facetValue) {
	const result = {
		group: facetGroups.find((facetGroup) => facetGroup.id === groupId),
		facet: null,
	};
	if (result.group) {
		result.facet = result.group.data.find(facet => facet.value === facetValue);
	}
	return result;
}

function getUncheckedFacetGroupPages(state) {
	const facetGroupPages = state.facetGroups?.filter(facetGroup => facetGroup.isPage) || [];
	const nonCheckedFacetGroupPages = facetGroupPages.filter(facetGroup => {
		return !facetGroup.data.find(facet => facet.checked);
	});
	return nonCheckedFacetGroupPages;
}

function hasUncheckedFacetGroupPages(state) {
	return getUncheckedFacetGroupPages(state).length > 0;
}

const facets = {
	state: () => ({
		showPrimaryCategoryFacets: true,
		anyFacets: false,
		apiUrl: '',
		searchApiAbortController: null,
		currentPage: 1,
		pageFacetSettings: [],
		facetGroups: [],
		facetGroupsProductPage: [],
		facetGroupsOnlyPrimaryCategory: [],
		loading: false,
		pagination: [],
		productsAmount: 8,
		results: [],
		searchTerm: '',
		corporateOnly: null,
		searchUsingHash: false,
		showOverlay: false,
		interactionStarted: false,
		productCategory: '',
		primaryCategoriesName: '',
		primaryCategoriesNameActiveItem: {},
		apiSearchResult: {},
		selectedManufacturer: null,
		selectedFacets: [],
		isInitializingFromQueryString: false,
		showAll: false,
		useReplaceState: false,
		keywordSearchInProgress: false,
		enableManufacturerTabs: false,
		firstSearchHasCompleted: false,
		restrictSearchToCorporateProducts: false,
		requireLoginToSearch: false,
		searchStatsMap: {},
		lastProductSearchQuery: '',
		didYouMeanItems: [],
	}),
	getters: {
		getAnyFacets: (state) => {
			return state.anyFacets;
		},
		getShowPrimaryCategoryFacets: (state) => {
			return state.showPrimaryCategoryFacets;
		},
		getPrimaryCategoriesName: (state) => {
			return state.primaryCategoriesName;
		},
		getPrimaryCategoriesNameActiveItem: (state) => {
			return state.primaryCategoriesNameActiveItem;
		},
		getPageFacetSettings: (state) => {
			return state.pageFacetSettings;
		},
		getPageFacetGroups: (state) => {
			return state.facetGroups.filter((facetGroup) => facetGroup.isPage);
		},
		getNonPageFacetGroups: (state) => {
			return state.facetGroups.filter((facetGroup) => !facetGroup.isPage);
		},
		getFacetGroupsProductPage: (state) => {
			return state.facetGroupsProductPage;
		},
		getFacetGroupsOnlyPrimaryCategory: (state) => {
			return state.facetGroupsOnlyPrimaryCategory;
		},
		getFacetGroups: (state) => {
			return state.facetGroups;
		},
		getPagination: (state) => {
			return state.pagination;
		},
		getCurrentPage: (state) => {
			return state.currentPage;
		},
		getResults: (state) => {
			return state.results;
		},
		getLoading: (state) => {
			return state.loading;
		},
		getSearchTerm: (state) => {
			return state.searchTerm;
		},
		getInteractionStarted: (state) => {
			return state.interactionStarted;
		},
		getProductCategory: (state) => {
			return state.productCategory;
		},
		getApiSearchResult: (state) => {
			return state.apiSearchResult;
		},
		getSelectedManufacturer: (state) => {
			return state.selectedManufacturer;
		},
		getSelectedFacets: (state) => {
			return state.selectedFacets;
		},
		getAreAllPageFacetsSelected: (state, getters) => {
			return getters.getPageFacetSettings.every((pageFacetSetting) => {
				return !!getters.getSelectedFacets.find((selectedFacet) => selectedFacet.groupId === pageFacetSetting.Id);
			});
		},
		getSearchQueryString: (state) => {
			let queryStringParts = [];
			if (state.currentPage) {
				queryStringParts.push(`currentPage=${state.currentPage}`);
			}
			if (state.searchTerm) {
				queryStringParts.push(`searchphrase=${prepareSearchPhrase(state.searchTerm)}`);
			}
			if (state.corporateOnly != null) {
				queryStringParts.push(`corporateOnly=${state.corporateOnly || state.restrictSearchToCorporateProducts}`);
			}
			if (state.selectedFacets.length > 0) {
				let facetParts = [];
				for (let facet of state.selectedFacets) {
					facetParts.push(`${facet.groupId}:${encodeURIComponent(facet.value)}`);
				}
				queryStringParts.push(`facetStrings=${facetParts.join(';')}`);
			}
			return `?${queryStringParts.join('&')}`;
		},
		getIsInitializingFromQueryString: (state) => state.isInitializingFromQueryString,
		getHasSelectedFacets: (state) => {
			return state.selectedFacets.length > 0;
		},
		getCurrentCategory: (state) => {
			if (state.selectedFacets.length == 0) {
				return null;
			}
			return state.selectedFacets[state.selectedFacets.length - 1];
		},
		getShowAll: (state) => state.showAll,
		getUseReplaceState: (state) => state.useReplaceState,
		getHasSelectedNonPrimaryFacets: (state) => {
			return state.selectedFacets.find((facet) => !facet.isPrimaryCategory);
		},
		getKeywordSearchInProgress: (state) => state.keywordSearchInProgress,
		getCorporateOnlyParam: (state) => state.corporateOnly,
		getEnableManufacturerTabs: (state) => state.enableManufacturerTabs,
		getFirstSearchHasCompleted: (state) => state.firstSearchHasCompleted,
		getRestrictSearchToCorporateProducts: (state) => state.restrictSearchToCorporateProducts,
		getRequireLoginToSearch: (state) => state.requireLoginToSearch,
		getLastProductSearchQuery: (state) => state.lastProductSearchQuery,
		getDidYouMeanItems: (state) => state.didYouMeanItems,
	},
	mutations: {
		setApiUrl: (state, apiUrl) => {
			state.apiUrl = apiUrl;
		},
		setShowPrimaryCategoryFacets: (state, showPrimaryCategoryFacets) => {
			state.showPrimaryCategoryFacets = showPrimaryCategoryFacets
		},
		setPrimaryCategoriesNameActiveItem: (state, primaryCategoriesNameActiveItem) => {
			state.primaryCategoriesNameActiveItem = primaryCategoriesNameActiveItem;
		},
		setPageFacetSettings: (state, pageFacetSettings) => {
			state.pageFacetSettings = pageFacetSettings;
		},
		setFacetGroups: (state, facetGroups) => {
			state.facetGroups = facetGroups;

			// go through all selected facets and make sure that the isPrimaryCategory property is
			// set accordingly to the matching facetGroup. This is necessary if the selected facets
			// has been initialized based on the browser query string. This can happen if a product list
			// is refrehsed or if the search page is accessed directly through a bookmark, a link, etc.
			for (const facet of state.selectedFacets) {
				const facetGroup = facetGroups.find((facetGroup) => facetGroup.id == facet.groupId);
				if (facetGroup) {
					facet.isPrimaryCategory = facetGroup.isPage;
				}
			}
		},
		setPrimaryCategoriesName: (state, primaryCategoriesName) => {
			state.primaryCategoriesName = primaryCategoriesName;
		},
		setPagination: (state, pagination) => {
			state.pagination = pagination;
		},
		setResults: (state, results) => {
			state.results = results;
		},
		setSearchTerm: (state, searchTerm) => {
			state.searchTerm = searchTerm;
			resetCurrentPageIfNeeded(state);
		},
		setAnyFacets: (state, anyFacets) => {
			state.anyFacets = anyFacets;
		},
		setFacetGroupsProductPage: (state, facetGroupsProductPage) => {
			state.facetGroupsProductPage = facetGroupsProductPage;
		},
		setFacetGroupsOnlyPrimaryCategory: (state, facetGroupsOnlyPrimaryCategory) => {
			state.facetGroupsOnlyPrimaryCategory = facetGroupsOnlyPrimaryCategory;
		},
		setCurrentPage: (state, currentPage) => {
			state.currentPage = currentPage;
		},
		setLoading: (state, loading) => {
			state.loading = loading;
		},
		setSearchUsingHash: (state, searchUsingHash) => {
			state.searchUsingHash = searchUsingHash;
		},
		setShowOverlay: (state, showOverlay) => {
			state.showOverlay = showOverlay;
		},
		setInteractionStarted: (state) => {
			state.interactionStarted = true;
		},
		setProductCategory: (state, category) => {
			state.productCategory = category;
		},
		setApiSearchResult: (state, apiSearchResult) => {
			state.apiSearchResult = apiSearchResult;
		},
		setSelectedManufacturer: (state, selectedManufacturer) => {
			state.selectedManufacturer = selectedManufacturer;
		},
		setSelectedFacets: (state, facets) => {
			state.selectedFacets = facets;
		},
		pushFacet: (state, { facet, groupId }) => {
			facet.groupId = groupId;
			let alreadySelected = state.selectedFacets.find(f => f.groupId === facet.groupId && f.value === facet.value);

			if (facet.checked && !alreadySelected) {
				state.selectedFacets.push(facet);
			}
			else if (!facet.checked && alreadySelected) {
				removeSelectedFacet(state, groupId);
			}
			resetCurrentPageIfNeeded(state);
		},
		popPrimaryCategoryFacet: (state) => {
			const newFacets = [];
			for (const facet of state.selectedFacets) {
				if (facet.isPrimaryCategory) {
					newFacets.push(facet);
				}
			}
			if (newFacets.length > 0) {
				newFacets.pop();
			}
			state.selectedFacets = newFacets;
			// remove the search term
			state.searchTerm = '';
			resetCurrentPageIfNeeded(state);
		},
		removeNonPageFacets: (state) => {
			const newFacets = [];
			for (const facet of state.selectedFacets) {
				if (facet.isPrimaryCategory) {
					newFacets.push(facet);
				}
			}
			state.selectedFacets = newFacets;

			for (const facetGroup of state.facetGroups) {
				if (facetGroup.isPage) {
					continue;
				}
				for (const facetItem of facetGroup.data) {
					if (facetItem.checked) {
						facetItem.checked = false;
					}
				}
			}

			resetCurrentPageIfNeeded(state);
		},
		deselectFacet: (state, facetId) => {
			removeSelectedFacet(state, facetId);
			resetCurrentPageIfNeeded(state);
		},
		setIsInitializingFromQueryString: (state, value) => {
			state.isInitializingFromQueryString = value;
		},
		setShowAll: (state, value) => {
			state.showAll = value;
		},
		setUseReplaceState: (state, value) => {
			state.useReplaceState = value;
		},
		setKeywordSearchInProgress: (state, value) => {
			state.keywordSearchInProgress = value;
		},
		setCorporateOnlyParam: (state, value) => {
			state.corporateOnly = value;
		},
		setEnableManufacturerTabs: (state, value) => state.enableManufacturerTabs = value,
		setFirstSearchCompleted: (state) => state.firstSearchHasCompleted = true,	// use this to keep track of if any search has completed
		setRestrictSearchToCorporateProducts: (state, value) => state.restrictSearchToCorporateProducts = value,
		setRequireLoginToSearch: (state, value) => state.requireLoginToSearch = value,
		setSearchStatsMap: (state, { term, id }) => {
			if (term) {
				state.searchStatsMap[term] = id;
			}
		},
	},
	actions: {
		search: ({ dispatch, commit, state }, params) => {;
			console.warn('search action is deprecated');
			const activeParams   = [],
				  selectedFacets = {};
			let aFacets = [];
			let facetStrings = '';
			let doEmptyKeywordSearch = false;

			commit('setAnyFacets', false);
			if (params.hasOwnProperty('term') && params.term === '') {
				commit('setSearchTerm', '');
				doEmptyKeywordSearch = true;
			}
			if (params.term || doEmptyKeywordSearch) {
				commit('setAnyFacets', false);
				commit('setSearchTerm', params.term);
			}
			if (params.facets) {
				// if the params has a non-empty facet string then use that for the search
				facetStrings = params.facets;
				commit('setAnyFacets', true);
			} else if (state.facetGroups && !params.noPushState) {
				// if the params.facets string is empty and params.noPushState is true it means we are moving back through the history
				// and we have reached the state where no facets have been selected. In that case, we should not perform the search
				// based on the facets that are selected in the UI. So we skip this code block.
				state.facetGroups.forEach(function (group) {
					group.data.forEach(function (item) {
						if (item.checked) {

							if (group.id != parameters.sort) {
								commit('setAnyFacets', true);
							}
							if (!selectedFacets.hasOwnProperty(group.id)) {
								selectedFacets[group.id] = [];
							}
							selectedFacets[group.id].push(encodeURIComponent(item.value));
						}
					});
				});

				for (let key in selectedFacets) {
					if (selectedFacets.hasOwnProperty(key)) {
						aFacets[aFacets.length] =
							encodeURIComponent(key) + ":" + selectedFacets[key];
					}
				}
				facetStrings = aFacets.join(';');
			}

			if (params.goToFirstPage) {
				commit('setCurrentPage', 1);
			}

			if (params.additionalParams) {
				activeParams.push(params.additionalParams);
			}

			if (state.searchTerm && state.searchTerm !== '') {
				activeParams.push(`searchphrase=${state.searchTerm}`);
			}

			let queryString = parameters.page + '=' + (params.currentPage || state.currentPage);

			if (activeParams.length > 0) {
				queryString += '&' + activeParams.join('&').replace(/%20/g, '+');
			}
			queryString += '&facetStrings=' + facetStrings;
			queryString = '?&' + queryString;

			if (!state.searchUsingHash || (activeParams.length === 0 && !facetStrings)) {
				return dispatch('searchWithQuery', { query: queryString });
			}
			else {
				window.location.hash = encodeURIComponent(queryString);
				return null;
			}
		},

		resetSearch: ({dispatch, state}) => {
			if (state.searchUsingHash) {
				window.location.hash = '';
				return null;
			}
			else {
				const promise = dispatch('search', {
					term: '',
				});
				dispatch('resetFacets')
				return promise;
			}
		},

		goBackOnePrimaryFacet: ({ dispatch, commit }) => {
			commit('popPrimaryCategoryFacet');
			commit('setCorporateOnlyParam', null);
			dispatch('searchBasedOnComputedQueryString');
		},

		resetNonPageFacets: ({ dispatch, commit }) => {
			commit('removeNonPageFacets');
			dispatch('searchBasedOnComputedQueryString');
		},

		toggleFacet: ({ dispatch, commit, state }, { facet, groupId }) => {
			// check if there are any unchecked facetGroup pages before toggling this facet
			const hasUncheckedFacetGroupPriorToToggle = hasUncheckedFacetGroupPages(state);

			facet.checked = !facet.checked;
			// toggle the facet
			commit('pushFacet', {
				facet,
				groupId,
			});

			// now check if there are any unchecked facetGroup page after toggling this facet
			const hasUncheckedFacetGroupAfterToggle = hasUncheckedFacetGroupPages(state);

			// if there were unchecked before the toggle and none after then the user just checked a facet on
			// the last facetGroup page. That means we need to make sure that we only search for corporate
			// products if the product tabs are enabled
			if (state.enableManufacturerTabs && hasUncheckedFacetGroupPriorToToggle && !hasUncheckedFacetGroupAfterToggle) {
				dispatch('setFacetGroupToAll', { id: "Manufacturer"});
				commit('removeNonPageFacets');
				commit('setCorporateOnlyParam', true);
			}

			dispatch('searchBasedOnComputedQueryString');
		},

		setFacetGroupToAll: ({ commit, state }, facetGroup) => {
			const isAlreadySet = state.selectedFacets.find((f) => {
				return f.groupId == facetGroup.id
			});

			if ( isAlreadySet ) {
				isAlreadySet.name = 'All';
				isAlreadySet.value = 'All';
			} else {
				commit('pushFacet', {
					facet: {
						name: 'All',
						value: 'All',
						checked: true,
						isPrimaryCategory: facetGroup.isPage,
						groupId: facetGroup.id,
					},
					groupId: facetGroup.id,
				});
			}
		},

		initializeSearchFromQueryString: ({ dispatch, commit }) => {
			commit('setIsInitializingFromQueryString', true);
			dispatch('setSelectedFacetsBasedOnQueryString');
			dispatch('searchBasedOnComputedQueryString');
			dispatch('setKeywordSearchInProgressBasedOnQueryString');
		},

		searchBasedOnComputedQueryString: ({ dispatch, getters, state }) => {
			let query = getters.getSearchQueryString.replace('?', '&');
			let corporateOnlyRegex = /(&corporateonly=([^&]|$)*)/i;
			if (state.restrictSearchToCorporateProducts) {
				// remove any corporateOnly parameter from the query string and replace it with corporateonly=true
				query.replace(corporateOnlyRegex, '');
				query = query + '&corporateOnly=true';
			}
			const isPageFacetsOnly = !state.searchTerm && !getters.getAreAllPageFacetsSelected;
			dispatch('searchWithQuery', { query, isPageFacetsOnly });
			if (state.useReplaceState) {
				if (state.restrictSearchToCorporateProducts) {
					// if corporateOnly then remove the corporateOnly parameter before updating the browser query string
					query.replace(corporateOnlyRegex, '');
				}
				history.replaceState(getters.getSearchQueryString, null, getters.getSearchQueryString);
			}
		},

		setKeywordSearchInProgressBasedOnQueryString: ({ commit }) => {
			const params = new URLSearchParams(window.location.search);
			if (params.get('searchphrase')) {
				commit('setKeywordSearchInProgress', true);
			}
		},

		setSelectedFacetsBasedOnQueryString: ({ commit, getters }) => {
			const params = new URLSearchParams(window.location.search);

			const currentPage = params.get('currentPage');
			if (currentPage) {
				commit('setCurrentPage', currentPage)
			}

			const searchTerm = params.get('searchphrase');
			if (searchTerm) {
				commit('setAnyFacets', false);
				commit('setSearchTerm', searchTerm);
				//return;
			}

			const corporateOnly = params.get("corporateOnly");
			if ( corporateOnly != null ) {
				commit('setCorporateOnlyParam', params.get("corporateOnly") === 'true');
			}

			const facetStrings = params.get('facetStrings');
			let facetStringFragments = [];
			if (facetStrings) {
				facetStringFragments = facetStrings.split(';');
			}
			facetStringFragments.forEach((facetString) => {
				const [groupId, facetValue] = facetString.split(':');
				let { facet } = findGroupAndFacet(getters.getFacetGroups, groupId, facetValue);

				// If this is executed on page load then we haven't performed a search yet and the getters.getFacetGroups will be empty and the
				// facet will be undefined. In this case create a faux facet that can then be added to the selected facets array. If we don't,
				// then the initial search will not take the facets in the query string into consideration.
				if (!facet) {
					facet = {
						name: facetValue,
						value: facetValue,
						checked: true,
						groupId: groupId,
					}
				}
				if (facet) {
					commit('pushFacet', {
						facet,
						groupId,
					});
				}
			})
		},

		keywordSearch: ({ commit, dispatch }, keyword) => {
			commit('setSearchTerm', keyword);
			dispatch('searchBasedOnComputedQueryString');
			commit('setKeywordSearchInProgress', true);
		},

		setShowAllBasedOnComputedQueryString: ({ commit, getters }) => {
			const queryString = getters.getSearchQueryString;
			commit('setShowAll', /\:all(&|$)/i.test(queryString));
		},

		searchWithQuery: ({ dispatch, commit, state, getters }, params) => {
			const { query, term, isPageFacetsOnly = false } = params;
			let queryString = query;
			if (/\?/.test(state.apiUrl)) {
				queryString = queryString.replace('?', '&');
			}
			let apiUrl = state.apiUrl + queryString;
			if (isPageFacetsOnly) {
				const toBeDisplayedFacetIds = [];
				const pageFacetSettingsSortedByPageOrder = getters.getPageFacetSettings.sort((a, b) => a.PageOrder - b.PageOrder);

				for (const pageFacetSetting of pageFacetSettingsSortedByPageOrder) {
					toBeDisplayedFacetIds.push(pageFacetSetting.Id);

					const isPageFacetSelected = !!getters.getSelectedFacets.find((selectedFacet) => selectedFacet.groupId === pageFacetSetting.Id);
					if (!isPageFacetSelected) {
						break;
					}
				}

				apiUrl = apiUrl.replace(
					/(?<=[&?])ToBeDisplayedFacets=([^&]|$)*/i,
					`ToBeDisplayedFacets=${toBeDisplayedFacetIds.join(',')}`
				);
				apiUrl += '&facetGroupsOnly=true';
			}

			commit('setLoading', true);
			commit('setKeywordSearchInProgress', false);

			if (state.searchApiAbortController) {
				state.searchApiAbortController.abort();
			}
			state.searchApiAbortController = new AbortController();

			return axios.get(apiUrl, {
				signal: state.searchApiAbortController.signal,
			})
				.then(response => {
					state.searchApiAbortController = null;
					if (response.data) {
						// if we have:
						//     1. not yet completed the first search
						//     2. manufacturer tabs are enabled
						//     3. no facet groups that are configured as pages are returned
						// or:
						//     1. the search is restricted to search for corporate products
						// then we need to perform the seach again, but this time search for corporate
						// only products. If we don't do this, we cannot initialize the search result
						// correctly so it only shows corporate products.
						if (
							!state.firstSearchHasCompleted &&
							state.enableManufacturerTabs &&
							!response.data?.facetGroups?.some(facetGroup => facetGroup.isPage
								||
							state.restrictSearchToCorporateProducts)
						) {
							// so set the firstSearchCompleted boolean so we don't go into an infinte loop.
							// then set the corporate only parameter to true so the next search only returns corporate products.
							// and then start the search again.
							commit('setFirstSearchCompleted');
							commit('setCorporateOnlyParam', true);
							dispatch('searchBasedOnComputedQueryString');
							return;
						}

						if (response.data.facetGroups) {
							commit('setFacetGroups', response.data.facetGroups);
						}

						commit('setSelectedManufacturer', response.data.selectedManufacturer);

						commit('setResults', response.data.results || []);
						commit('setPagination', response.data.pagination);
						commit('setApiSearchResult', response.data);

						if (response.data.didYouMeanItems) {
							state.didYouMeanItems = response.data.didYouMeanItems;
						} else {
							state.didYouMeanItems = [];
						}

						// Track in epifind
						const searchQueryParams = new URLSearchParams(query);
						const searchQuery = searchQueryParams.get("searchphrase");
						dispatch('trackProductSearchStats', { searchQuery, itemCount: response.data.results?.length });


						// we have gotten this far, so make sure to register that we have completed the first search
						commit('setFirstSearchCompleted');
						dispatch('setShowAllBasedOnComputedQueryString');
					}
					// the following statements should be in a finally clause but there is a problem transpiling those for IE
					commit('setShowOverlay', false);
					commit('setLoading', false);
					commit('setAnyFacets', false);
					if (state.facetGroups && !term) {
						state.facetGroups.forEach(function (group) {
							group.data.forEach(function (item) {
								if (item.checked && group.id != parameters.sort) {
									if(group.id === state.primaryCategoriesName) {
										commit('setShowPrimaryCategoryFacets', false);
										commit('setPrimaryCategoriesNameActiveItem', item);
									}
									if (!group.isPage && !item.isPrimaryCategory) {
										commit('setAnyFacets', true);
									}

									commit('pushFacet', {
										facet: item,
										groupId: group.id,
										group: group,
									});
								}
							});
						});
					}
					commit('setIsInitializingFromQueryString', false);
				})
				.catch(error => {
					if (!axios.isCancel(error)) {
						commit('setLoading', false);
						state.searchApiAbortController = null;
						console.warn(error);
					}
				})
		},
		setCurrentPage: ({ dispatch, commit }, page) => {
			commit('setCurrentPage', page);
			return dispatch('searchBasedOnComputedQueryString');
		},

		setPrimaryCategoriesName: ({ commit }, name) => {
			commit('setPrimaryCategoriesName', name);
		},

		setFacetGroupsProductPage: ({commit, state}) => {
			const facetsWithoutPrimaryCategory = state.facetGroups.filter((item) => item.id !== state.primaryCategoriesName);

			commit('setFacetGroupsProductPage', facetsWithoutPrimaryCategory);
		},

		setFacetGroupsOnlyPrimaryCategory: ({commit, state}) => {
			const facetsGroupsOnlyPrimaryCategory = state.facetGroups.filter((item) => item.id === state.primaryCategoriesName);

			commit('setFacetGroupsOnlyPrimaryCategory', facetsGroupsOnlyPrimaryCategory);
		},

		resetFacets: ({ dispatch, state, commit }, payload = null) => {
			state.facetGroups.forEach(function (group) {
				group.data.forEach(function (item) {
					item.checked = false;
				});
			});
			commit('setFacetGroups', state.facetGroups);
			commit('setSelectedManufacturer', null);
			commit('setShowPrimaryCategoryFacets', true);

			let params =  { goToFirstPage: true };
			if ( payload ) {
				params = {...params, ...payload};
			}

			return dispatch('search', params);
		},

		resetFacetsProductPage: ({ dispatch, state, commit }) => {
			state.facetGroupsProductPage.forEach(function (group) {
				group.data.forEach(function (item) {
					item.checked = false;
				});
			});
			commit('setFacetGroupsProductPage', state.facetGroupsProductPage);
			commit('setSelectedManufacturer', null);
			return dispatch('search', { goToFirstPage: true });
		},
		paginationChange: ({ dispatch, commit }, page) => {
            commit('setCurrentPage', page);
			dispatch('searchBasedOnComputedQueryString');
		},
		trackProductSearchStats: async ({ commit, state }, { searchQuery, itemCount }) => {
			state.lastProductSearchQuery = searchQuery;
			const id = await trackSearchResultImpression(searchQuery, itemCount);
			commit('setSearchStatsMap', { term: searchQuery, id });
		},
		trackProductSearchClick: ({ state }, { searchQuery, itemGuid }) => {
			const trackId = state.searchStatsMap[searchQuery];
			if (trackId) {
				trackSearchResultClick({ query: searchQuery, itemGuid, trackId });
			} else {
				console.warn(`[Epifind]: No track Id found for the search query: ${searchQuery}`);
			}
		},
	}
}

export default facets;
